import React, { useMemo, useRef, useState } from "react"
import { COUNTRY_CODES } from "../const/countryCodes"

const LOCALES = { arm: "hy", eng: "en", fr: "fr", arb: "ar", rus: "ru" }
const PRIORITY_COUNT = 10

const localizedNames = (lang) => {
    try {
        const names = new Intl.DisplayNames([LOCALES[lang] || "en"], { type: "region" })
        return (iso) => names.of(iso) || iso
    } catch (e) {
        return null
    }
}

// Dial code input with a filtered suggestion list: typing "3" suggests +374, +33, +30...
// Letters filter by country name instead. Calls onChange with the code digits.
const CountryCodeSelect = ({ value, onChange, onSelected, lang }) => {
    const [open, setOpen] = useState(false)
    const [highlight, setHighlight] = useState(0)
    const [selectedIso, setSelectedIso] = useState("AM")
    const listRef = useRef(null)

    const countries = useMemo(() => {
        const nameOf = localizedNames(lang)
        return COUNTRY_CODES.map((country, index) => ({
            ...country,
            label: nameOf ? nameOf(country.iso) : country.name,
            // the first entries of the list are the most used ones, keep them on top
            priority: index < PRIORITY_COUNT ? index : PRIORITY_COUNT
        }))
    }, [lang])

    const matches = useMemo(() => {
        const query = value.trim().toLowerCase()
        if (!query) return countries
        if (/^\d+$/.test(query)) {
            return countries
                .filter(country => country.code.startsWith(query))
                .sort((a, b) => a.priority - b.priority || a.code.length - b.code.length || a.code.localeCompare(b.code))
        }
        return countries.filter(country =>
            country.label.toLowerCase().includes(query) || country.name.toLowerCase().includes(query))
    }, [value, countries])

    const current = countries.find(country => country.iso === selectedIso && country.code === value)
        || countries.find(country => country.code === value)

    const select = (country) => {
        setSelectedIso(country.iso)
        onChange(country.code)
        setOpen(false)
        onSelected && onSelected()
    }

    const moveHighlight = (index) => {
        setHighlight(index)
        const item = listRef.current?.children[index]
        item && item.scrollIntoView({ block: "nearest" })
    }

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault()
            if (!open) return setOpen(true)
            moveHighlight(Math.min(highlight + 1, matches.length - 1))
        } else if (e.key === "ArrowUp") {
            e.preventDefault()
            moveHighlight(Math.max(highlight - 1, 0))
        } else if (e.key === "Enter" && open && matches[highlight]) {
            e.preventDefault()
            e.stopPropagation()
            select(matches[highlight])
        } else if (e.key === "Escape") {
            setOpen(false)
        }
    }

    return (
        <>
            <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                <p className="info-p">+</p>
                <input
                    type="text"
                    value={value}
                    onChange={(e) => {
                        onChange(e.target.value.replace(/^\+/, "").slice(0, 30))
                        setOpen(true)
                        setHighlight(0)
                    }}
                    onFocus={(e) => { e.target.select(); setOpen(true); setHighlight(0) }}
                    onBlur={() => setOpen(false)}
                    onKeyDown={onKeyDown}
                    className="info-input"
                    style={{ width: "58px" }}
                    autoComplete="off"
                    aria-label="Country code"
                />
                {current && <span style={{ fontSize: "11px", color: "#2C507B", opacity: 0.6, marginRight: "6px" }}>{current.iso}</span>}
                <span style={{ width: "1px", height: "20px", background: "rgba(44, 80, 123, 0.3)" }} />
            </div>
            {open && matches.length > 0 && (
                <ul
                    ref={listRef}
                    style={{
                        position: "absolute", top: "100%", left: "-1px", right: "-1px", zIndex: 20,
                        maxHeight: "220px", overflowY: "auto", background: "#FFFFFF",
                        border: "1px solid rgba(44, 80, 123, 0.5)", borderRadius: "4px",
                        boxShadow: "0 6px 16px rgba(0, 0, 0, 0.12)", margin: "2px 0 0", padding: "4px 0",
                        listStyle: "none", textAlign: "left"
                    }}
                >
                    {matches.map((country, index) => (
                        <li
                            key={country.iso}
                            onMouseDown={(e) => { e.preventDefault(); select(country) }}
                            onMouseEnter={() => setHighlight(index)}
                            style={{
                                display: "flex", justifyContent: "space-between", gap: "12px",
                                padding: "6px 12px", cursor: "pointer", color: "#2C507B", fontSize: "14px",
                                background: index === highlight ? "#F0F4FF" : "transparent"
                            }}
                        >
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {country.label}
                            </span>
                            <span style={{ fontWeight: 600, flexShrink: 0 }}>+{country.code}</span>
                        </li>
                    ))}
                </ul>
            )}
        </>
    )
}

export default CountryCodeSelect
